// src/pages/InvoiceDetail.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  Send,
  CreditCard,
  Ban,
  Eraser,
  FileDown,
  CalendarDays,
  User2,
  Building2,
  DollarSign,
  Percent,
  Edit2,
  Trash2,
  RefreshCw,
} from "lucide-react";

import api from "../api/axios";
import AddPaymentModal from "../components/AddPaymentModal";
import EditInvoiceModal from "../components/EditInvoiceModal";
import SendInvoiceModal from "../components/SendInvoiceModal";

import {
  voidInvoice,
  writeOffInvoice,
  deleteInvoice,
} from "../services/invoiceService";

// ✅ shared money utils (safe currency)
import { formatMoney, safeCurrency } from "../utils/money";

/* ----------------------------- helpers ----------------------------- */
const cx = (...a) => a.filter(Boolean).join(" ");
const fmtMoney = (n = 0, c = "USD") =>
  formatMoney(n, safeCurrency(c || "USD"));

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

/** Date formatter for timestamps (local time) — use for createdAt/updatedAt/activity */
const fmtDateLocal = (d) =>
  d
    ? new Date(d).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

/** Date+time formatter for timestamps (local time) — email stats etc. */
const fmtDateTimeLocal = (d) =>
  d
    ? new Date(d).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

/** 🔒 Date-only formatter locked to UTC — avoids off-by-one for issue/due */
const fmtDateOnlyUTC = (d) =>
  d
    ? new Date(d).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      })
    : "—";

/** Convert any date to a UTC "midnight" date-only epoch for safe day math */
const epochUTCDateOnly = (d) => {
  if (!d) return NaN;
  const x = new Date(d);
  return Date.UTC(x.getUTCFullYear(), x.getUTCMonth(), x.getUTCDate());
};

/** Day difference in whole days using UTC date-only (prevents TZ drift) */
const daysBetweenUTC = (a, b) =>
  Math.floor(
    (epochUTCDateOnly(a) - epochUTCDateOnly(b)) / (1000 * 60 * 60 * 24)
  );

/** Multiline address like QuoteDetails */
const fmtAddressMultiline = (addr) => {
  if (!addr) return "";
  if (typeof addr === "string") return addr;
  if (addr.raw) return addr.raw;

  const line1 = addr.line1 || addr.street || addr.street1 || "";
  const line2 = addr.line2 || addr.street2 || "";
  const city = addr.city || "";
  const region = addr.state || addr.region || addr.province || "";
  const postal = addr.postalCode || addr.zip || "";
  const country = addr.country || "";

  const rows = [];
  if (line1) rows.push(String(line1).trim());
  if (line2) rows.push(String(line2).trim());
  const cityRow = [city, region, postal].filter(Boolean).join(", ");
  if (cityRow) rows.push(cityRow);
  if (country) rows.push(country);

  return rows.join("\n");
};

const role = () => (localStorage.getItem("role") || "admin").toLowerCase();
const canMutateRole = () => ["admin", "staff"].includes(role());

// small helpers for discount math (aligned with InvoiceNew/InvoiceEdit)
const round2 = (n) =>
  Math.round((Number(n || 0) + Number.EPSILON) * 100) / 100;
const clamp = (val, min, max) =>
  Math.min(Math.max(Number(val || 0), min), max);

// 🔢 server-aligned line math: base subtotal + line discount + tax
function calcLine(li) {
  const qty = Math.max(0, Number(li.quantity || 0));
  const rate = Math.max(0, Number(li.rate ?? li.unitPrice ?? 0));
  const taxPct = clamp(li.taxPercent, 0, 100);
  const dPct = clamp(li.discountPercent, 0, 100);
  const dAmt = Math.max(0, Number(li.discountAmount || 0));
  const taxIncl = !!li.isTaxInclusive;

  let unitBase = rate;
  if (taxIncl && taxPct > 0) unitBase = rate / (1 + taxPct / 100);

  const afterDiscount =
    dAmt > 0
      ? Math.max(0, unitBase - dAmt)
      : unitBase * (1 - (dPct > 0 ? dPct / 100 : 0));

  // base subtotal (before discount)
  const subtotalBase = round2(qty * unitBase);
  // subtotal after discount (used for tax)
  const subtotal = round2(qty * afterDiscount);
  const lineDiscount = round2(Math.max(0, subtotalBase - subtotal));

  const taxAmount = round2(subtotal * (taxPct > 0 ? taxPct / 100 : 0));
  const total = round2(subtotal + taxAmount);

  return { subtotalBase, subtotal, taxAmount, total, lineDiscount };
}

/* ----------------------------- chips ----------------------------- */
function StatusChip({ status }) {
  const map = {
    paid: "bg-green-100 text-green-700 border-green-200",
    partial: "bg-amber-100 text-amber-700 border-amber-200",
    unpaid: "bg-slate-100 text-slate-700 border-slate-200",
    overdue: "bg-rose-100 text-rose-700 border-rose-200",
    void: "bg-zinc-100 text-zinc-600 border-zinc-200",
    written_off: "bg-zinc-100 text-zinc-700 border-zinc-200",
  };
  const cls = map[(status || "unpaid").toLowerCase()] || map.unpaid;
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold",
        cls
      )}
    >
      {(status || "unpaid").toUpperCase()}
    </span>
  );
}

/* ----------------------------- main ----------------------------- */
export default function InvoiceDetail() {
  const { id: routeId = "" } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [client, setClient] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  // modals
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showSend, setShowSend] = useState(false);

  /* ----------------------------- API fetchers ----------------------------- */
  const fetchInvoice = useCallback(async (id) => {
    try {
      const res = await api.get(`/invoices/${id}`);
      return res?.data?.data || null;
    } catch (err) {
      if (err?.response?.status === 400) toast.error("Invalid invoice id");
      return null;
    }
  }, []);

  const fetchClient = useCallback(async (clientId) => {
    if (!clientId) return null;
    try {
      const res = await api.get(`/clients/${clientId}`);
      return res?.data?.data || null;
    } catch {
      return null;
    }
  }, []);

  const fetchPayments = useCallback(async (invoiceId) => {
    if (!invoiceId) return [];
    try {
      const res = await api.get(`/payments`, { params: { invoiceId } });
      return Array.isArray(res?.data) ? res.data : [];
    } catch {
      return [];
    }
  }, []);

  /* ----------------------------- data load ----------------------------- */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const inv = await fetchInvoice(routeId);
      if (!inv) {
        setInvoice(null);
        setClient(null);
        setPayments([]);
        return;
      }
      setInvoice(inv);

      const [clientData, paymentData] = await Promise.all([
        fetchClient(inv.clientId),
        fetchPayments(inv._id),
      ]);
      setClient(clientData);
      setPayments(paymentData);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error("Failed to load invoice");
    } finally {
      setLoading(false);
    }
  }, [routeId, fetchInvoice, fetchClient, fetchPayments]);

  useEffect(() => {
    load();
  }, [load]);

  /* ----------------------------- computed ----------------------------- */
  const currency = safeCurrency(invoice?.currency || "USD");
  const details = invoice?.invoiceDetails || null;

  // One canonical snapshot for totals (prefers invoiceDetails, falls back to line items only)
  const computedSnapshot = useMemo(() => {
    const lis = invoice?.lineItems || [];

    if (details) {
      const subtotalBase = round2(Number(details.subtotalBase ?? 0));
      const taxTotal = round2(Number(details.taxTotal ?? 0));
      const lineDiscountTotal = round2(Number(details.lineDiscountTotal ?? 0));
      const invoiceDiscountTotal = round2(Number(details.discountTotal ?? 0));
      const totalAmount = round2(Number(details.totalAmount ?? 0));
      const amountPaid = round2(Number(details.amountPaid ?? 0));
      const balanceDue = round2(
        details.balanceDue != null
          ? Number(details.balanceDue)
          : Math.max(0, totalAmount - amountPaid)
      );

      return {
        subtotalBase,
        taxTotal,
        lineDiscountTotal,
        invoiceDiscountTotal,
        totalAmount,
        amountPaid,
        balanceDue,
      };
    }

    // Fallback: recompute from line items (line-level discounts only, no invoice-level discount)
    if (!lis.length) {
      return {
        subtotalBase: 0,
        taxTotal: 0,
        lineDiscountTotal: 0,
        invoiceDiscountTotal: 0,
        totalAmount: 0,
        amountPaid: 0,
        balanceDue: 0,
      };
    }

    let subtotalBase = 0;
    let taxTotal = 0;
    let lineDiscountTotal = 0;
    let totalAmount = 0;

    for (const li of lis) {
      const c = calcLine(li);
      subtotalBase += c.subtotalBase;
      taxTotal += c.taxAmount;
      lineDiscountTotal += c.lineDiscount || 0;
      totalAmount += c.total;
    }

    subtotalBase = round2(subtotalBase);
    taxTotal = round2(taxTotal);
    lineDiscountTotal = round2(lineDiscountTotal);
    totalAmount = round2(totalAmount);

    return {
      subtotalBase,
      taxTotal,
      lineDiscountTotal,
      invoiceDiscountTotal: 0,
      totalAmount,
      amountPaid: 0,
      balanceDue: totalAmount,
    };
  }, [invoice, details]);

  const {
    subtotalBase,
    taxTotal,
    lineDiscountTotal,
    invoiceDiscountTotal,
    totalAmount: serverTotal,
    amountPaid: serverAmountPaid,
    balanceDue: serverBalance,
  } = computedSnapshot;

  // Combined display = line-level + invoice-level (from invoiceDetails)
  const combinedDiscountDisplay = round2(
    (lineDiscountTotal || 0) + (invoiceDiscountTotal || 0)
  );

  const computedTotal =
    serverTotal ||
    round2(Math.max(0, subtotalBase - combinedDiscountDisplay + taxTotal));

  const totalPaid = serverAmountPaid;
  const balance =
    Number.isFinite(serverBalance) && serverBalance !== 0
      ? serverBalance
      : round2(Math.max(0, computedTotal - totalPaid));

  // alias for UI – this is the pre-discount subtotal
  const clientSubtotal = subtotalBase;

  // Payments with running balance
  const paymentsSorted = useMemo(
    () =>
      [...payments].sort(
        (a, b) => new Date(a.paymentDate) - new Date(b.paymentDate)
      ),
    [payments]
  );
  const paymentsWithBalance = useMemo(() => {
    let paid = 0;
    return paymentsSorted.map((p) => {
      paid += Number(p?.amount || 0);
      const runningBalance = Math.max(0, computedTotal - paid);
      return { ...p, runningBalance };
    });
  }, [paymentsSorted, computedTotal]);

  // 🔧 Dates & status (UTC date-only to avoid header off-by-one)
  const now = new Date();
  const dueDateRaw = invoice?.dueDate || null;
  const deltaDue = dueDateRaw ? daysBetweenUTC(dueDateRaw, now) : null; // +ve = in future, 0 = today, -ve = overdue
  const dueBadge = useMemo(() => {
    if (deltaDue === null) return null;
    if (deltaDue > 0) return `Due in ${deltaDue}d`;
    if (deltaDue === 0) return "Due today";
    return `Overdue ${Math.abs(deltaDue)}d`;
  }, [deltaDue]);

  const isOverdue = useMemo(
    () => (dueDateRaw ? daysBetweenUTC(dueDateRaw, now) < 0 : false),
    [dueDateRaw, now]
  );

  const derivedStatus = useMemo(() => {
    if (!invoice) return "unpaid";

    // lifecycle overrides (void / written_off)
    const lc = String(invoice.lifecycleStatus || "").toLowerCase();
    if (lc === "void") return "void";
    if (lc === "written_off") return "written_off";

    // ✅ Prefer backend-computed invoice.status if present
    const s = String(invoice.status || "").toLowerCase();
    if (s) return s;

    // Fallback to client-side computation
    if (totalPaid >= computedTotal) return "paid";
    if (totalPaid > 0 && totalPaid < computedTotal) return "partial";
    if (isOverdue) return "overdue";
    return "unpaid";
  }, [invoice, totalPaid, computedTotal, isOverdue]);

  // Lifecycle locks
  const lifecycle = String(invoice?.lifecycleStatus || "").toLowerCase();
  const isEditLocked = ["sent", "void", "written_off"].includes(lifecycle);
  const isPaymentLocked = ["void", "written_off"].includes(lifecycle);
  const isSendLocked = ["void", "written_off"].includes(lifecycle);
  const canEdit = canMutateRole() && !isEditLocked;

  // 🔧 Terms using UTC date-only math
  const terms = useMemo(() => {
    if (!invoice?.issueDate || !invoice?.dueDate)
      return invoice?.terms || "—";
    const d = daysBetweenUTC(invoice.dueDate, invoice.issueDate);
    return d > 0 ? `Net ${d}` : invoice?.terms || "—";
  }, [invoice]);

  // Tax breakdown (schema-level taxAmount per line)
  const taxGroups = useMemo(() => {
    const m = new Map();
    (invoice?.lineItems || []).forEach((li) => {
      const pct = Number(li?.taxPercent || 0);
      const key = Number.isFinite(pct) ? pct : 0;
      const amt = Number(li?.taxAmount || 0);
      m.set(key, (m.get(key) || 0) + amt);
    });
    return m;
  }, [invoice]);

  const nonZeroTaxPercents = [...taxGroups.keys()].filter((p) => p > 0);
  const taxHeaderLabel =
    nonZeroTaxPercents.length === 1
      ? `Tax (${nonZeroTaxPercents[0]}%)`
      : "Tax";
  const taxBreakdownRows = [...taxGroups.entries()]
    .filter(([pct, amt]) => pct > 0 && amt > 0)
    .sort((a, b) => a[0] - b[0]);

  // Company snapshot (bill from)
  const co = useMemo(() => {
    const bf = invoice?.billFrom || {};
    const hasBF =
      !!(
        bf?.name ||
        bf?.email ||
        bf?.phone ||
        bf?.taxId ||
        bf?.logoUrl ||
        (bf?.address && Object.values(bf.address).some(Boolean))
      );
    return hasBF ? bf : invoice?.companySnapshot || {};
  }, [invoice]);

  const fmtCoAddress = (a) => fmtAddressMultiline(a) || "—";

  // -------- Bill To (company + contact + address) ----------
  const billToCompany =
    invoice?.clientSnapshot?.name ||
    client?.name ||
    invoice?.clientName ||
    "—";
  const billToContactName =
    invoice?.clientSnapshot?.contactName ||
    client?.primaryContact?.name ||
    client?.contactName ||
    client?.contact?.fullName ||
    client?.contact?.name ||
    "";

  const billToEmail =
    invoice?.clientSnapshot?.email ||
    client?.primaryContact?.email ||
    client?.email ||
    client?.contact?.email ||
    "";
  const billToPhone =
    invoice?.clientSnapshot?.phone ||
    client?.primaryContact?.phone ||
    client?.phone ||
    client?.contact?.phone ||
    "";

  const billToAddress =
    invoice?.clientSnapshot?.address ||
    client?.billingAddress ||
    client?.address ||
    invoice?.billTo?.address ||
    null;

  const billToAddressText = fmtAddressMultiline(billToAddress);

  // -------- Email activity computed ----------
  const emailStats = invoice?.emailStats || {};
  const lastSentAt = emailStats?.lastSentAt || invoice?.sentAt || null;

  const viewCount =
    (invoice?.public && Number(invoice.public.viewCount)) ||
    (invoice?.viewStats && Number(invoice.viewStats.count)) ||
    0;
  const lastViewAt =
    invoice?.public?.lastViewAt ||
    invoice?.viewStats?.lastAt ||
    invoice?.viewedAt ||
    null;

  const deliveredCount = Number.isFinite(emailStats?.deliveredCount)
    ? Number(emailStats.deliveredCount)
    : null;
  const bouncedCount = Number.isFinite(emailStats?.bouncedCount)
    ? Number(emailStats.bouncedCount)
    : null;
  const droppedCount = Number.isFinite(emailStats?.droppedCount)
    ? Number(emailStats.droppedCount)
    : null;
  const complainedCount = Number.isFinite(emailStats?.complainedCount)
    ? Number(emailStats.complainedCount)
    : null;
  const failedCount = Number.isFinite(emailStats?.failedCount)
    ? Number(emailStats.failedCount)
    : null;

  const openCount = Number.isFinite(emailStats?.openCount)
    ? Number(emailStats.openCount)
    : null;
  const clickCount = Number.isFinite(emailStats?.clickCount)
    ? Number(emailStats.clickCount)
    : null;

  const lastDeliveredAt =
    emailStats?.lastDeliveredAt || emailStats?.deliveredAt || null;
  const lastBouncedAt =
    emailStats?.lastBouncedAt || emailStats?.bouncedAt || null;
  const lastDroppedAt =
    emailStats?.lastDroppedAt || emailStats?.droppedAt || null;
  const lastComplainedAt =
    emailStats?.lastComplainedAt || emailStats?.complainedAt || null;
  const lastFailedAt =
    emailStats?.lastFailedAt || emailStats?.failedAt || null;

  const lastOpenAt = emailStats?.lastOpenAt || emailStats?.openedAt || null;
  const lastClickAt = emailStats?.lastClickAt || emailStats?.clickedAt || null;

  const recipientsCount = Number.isFinite(emailStats?.recipientsCount)
    ? Number(emailStats.recipientsCount)
    : Array.isArray(emailStats?.recipients)
    ? emailStats.recipients.length
    : null;

  // Linked Records
  const linkedWork = useMemo(() => {
    const w = invoice?.workId;
    if (!w) return null;
    if (typeof w === "string")
      return { _id: w, workNumber: null, status: null, type: null };
    return {
      _id: w?._id || w?.id,
      workNumber: w?.workNumber,
      status: w?.status,
      type: w?.type,
    };
  }, [invoice]);

  const linkedQuote = useMemo(() => {
    const q = invoice?.quoteId;
    if (!q) return null;
    if (typeof q === "string")
      return { _id: q, quoteNumber: null, status: null, totalAmount: null };
    return {
      _id: q?._id,
      quoteNumber: q?.quoteNumber,
      status: q?.status,
      totalAmount: q?.totalAmount,
    };
  }, [invoice]);

  /* ----------------------------- actions ----------------------------- */
  const onVoid = async () => {
    if (!invoice || ["void", "written_off"].includes(lifecycle)) return;
    if (!window.confirm("Void this invoice? This cannot be undone.")) return;
    try {
      await voidInvoice(invoice._id);
      toast.success("Invoice voided");
      load();
    } catch {
      toast.error("Failed to void");
    }
  };

  const onWriteOff = async () => {
    if (!invoice || ["void", "written_off"].includes(lifecycle)) return;
    const amt = window.prompt(
      "Write-off amount (note: lifecycle only):",
      `${balance}`
    );
    if (amt === null) return;
    const n = Number(amt || balance);
    if (Number.isNaN(n) || n < 0) return toast.error("Invalid amount");
    try {
      await writeOffInvoice(invoice._id, {
        amount: n,
        reason: "Manual write-off",
      });
      toast.success("Write-off recorded");
      load();
    } catch {
      toast.error("Failed to write off");
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this invoice permanently?")) return;
    try {
      await deleteInvoice(invoice._id);
      toast.success("Invoice deleted");
      navigate("/invoices");
    } catch {
      toast.error("Failed to delete");
    }
  };

  // Download-only PDF (no new tab, no preview)
  const onPdf = async () => {
    try {
      if (!invoice?._id) throw new Error("Missing invoice id");

      const fileName = `${invoice.invoiceNumber || invoice._id || "invoice"}.pdf`.replace(
        /[^\w.-]+/g,
        "_"
      );
      const resp = await api.get(`/invoices/${invoice._id}/pdf`, {
        responseType: "blob",
        params: {
          download: 1,
          v: Date.now(),
        },
      });

      const blob = resp?.data;
      const ct = String(resp?.headers?.["content-type"] || "").toLowerCase();

      if (ct.includes("application/json")) {
        const text = await blob.text?.();
        try {
          const json = JSON.parse(text || "{}");
          throw new Error(json?.error || "Server error while generating PDF");
        } catch {
          throw new Error(text || "Server error while generating PDF");
        }
      }

      if (!(blob instanceof Blob) || blob.size === 0)
        throw new Error("Empty PDF response");

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error(e?.message || "Unable to download PDF");
    }
  };

  const onRefreshCompanySnapshot = async () => {
    try {
      await api.put(`/invoices/${invoice._id}`, {
        refreshCompanySnapshot: true,
      });
    } catch (e) {
      toast.error(
        e?.response?.data?.error || "Failed to refresh company info"
      );
      return;
    }
    toast.success("Company info refreshed on invoice");
    load();
  };

  /* ----------------------------- renderers ----------------------------- */
  const MoneyStat = ({ label, value, icon: Icon }) => (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 text-slate-600 text-sm">
        {Icon ? <Icon size={16} /> : null}
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold text-slate-900">
        {fmtMoney(value, currency)}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="w-full p-6">
        <div className="animate-pulse rounded-2xl bg-slate-100 h-40 mb-6" />
        <div className="animate-pulse rounded-2xl bg-white h-[70vh] shadow" />
      </div>
    );
  }
  if (!invoice) return <div className="p-6">Invoice not found.</div>;

  return (
    <div className="w-full min-h-[90vh] py-8 px-3 sm:px-6 lg:px-8 bg-slate-50">
      <div className="w-full rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden print:bg-white print:shadow-none print:border-0">
        {/* Header (match QuoteDetails look) */}
        <div className="p-4 sm:p-5 border-b border-slate-200 bg-white">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-slate-700 hover:bg-slate-50"
              >
                <ArrowLeft size={16} />
                Back
              </button>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-slate-900 text-xl sm:text-2xl font-semibold mr-1">
                    {invoice.invoiceNumber ||
                      (invoice._id
                        ? `INV-${String(invoice._id).slice(-6)}`
                        : "")}
                  </h1>
                  <StatusChip status={derivedStatus} />
                  {dueBadge ? (
                    <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                      {dueBadge}
                    </span>
                  ) : null}
                </div>
                <p className="text-slate-600 text-sm mt-1">
                  Invoice details, totals, and payment activity
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => (!isSendLocked ? setShowSend(true) : null)}
                disabled={isSendLocked}
                title={
                  isSendLocked
                    ? "Cannot send a void or written-off invoice"
                    : "Send invoice via email"
                }
                className={cx(
                  "rounded-xl border px-3 py-2",
                  isSendLocked
                    ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                    : "border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700"
                )}
              >
                <Send size={16} className="inline -mt-0.5" />{" "}
                <span className="ml-1 hidden sm:inline">Send</span>
              </button>
              <button
                onClick={onPdf}
                className="rounded-xl border border-slate-200 px-3 py-2 text-slate-700 hover:bg-slate-50"
              >
                <FileDown size={16} className="inline -mt-0.5" />{" "}
                <span className="ml-1 hidden sm:inline">PDF</span>
              </button>

              {canMutateRole() && (
                <div className="hidden sm:flex items-center gap-2">
                  {/* Full Edit */}
                  <button
                    onClick={() =>
                      !isEditLocked
                        ? navigate(`/invoices/${invoice._id}/edit`)
                        : null
                    }
                    disabled={isEditLocked}
                    title={
                      isEditLocked
                        ? "Locked after Send/Void/Write-off"
                        : "Full Edit"
                    }
                    className={cx(
                      "rounded-xl px-3 py-2 border",
                      isEditLocked
                        ? "border-slate-200 text-slate-400 cursor-not-allowed opacity-50"
                        : "border-slate-200 text-slate-700 hover:bg-slate-50"
                    )}
                  >
                    <Edit2 size={16} className="inline -mt-0.5" />
                    <span className="ml-1">Full Edit</span>
                  </button>

                  {/* Record payment */}
                  <button
                    onClick={() =>
                      !isPaymentLocked ? setShowAddPayment(true) : null
                    }
                    disabled={isPaymentLocked}
                    title={
                      isPaymentLocked
                        ? "Payments are disabled on void/written-off invoices"
                        : "Record payment"
                    }
                    className={cx(
                      "rounded-xl px-3 py-2 text-white",
                      isPaymentLocked
                        ? "bg-slate-300 cursor-not-allowed"
                        : "bg-green-600 hover:bg-green-700"
                    )}
                  >
                    <CreditCard size={16} className="inline -mt-0.5" />{" "}
                    <span className="ml-1">Record</span>
                  </button>

                  {/* Write-off */}
                  <button
                    onClick={onWriteOff}
                    disabled={["void", "written_off"].includes(lifecycle)}
                    title={
                      ["void", "written_off"].includes(lifecycle)
                        ? "Already void/written-off"
                        : "Write-off"
                    }
                    className={cx(
                      "rounded-xl px-3 py-2 text-white",
                      ["void", "written_off"].includes(lifecycle)
                        ? "bg-slate-300 cursor-not-allowed"
                        : "bg-rose-600 hover:bg-rose-700"
                    )}
                  >
                    <Eraser size={16} className="inline -mt-0.5" />{" "}
                    <span className="ml-1">Write-off</span>
                  </button>

                  {/* Void */}
                  <button
                    onClick={onVoid}
                    disabled={["void", "written_off"].includes(lifecycle)}
                    title={
                      ["void", "written_off"].includes(lifecycle)
                        ? "Already void/written-off"
                        : "Void"
                    }
                    className={cx(
                      "rounded-xl px-3 py-2 text-white",
                      ["void", "written_off"].includes(lifecycle)
                        ? "bg-slate-300 cursor-not-allowed"
                        : "bg-zinc-700 hover:bg-black/80"
                    )}
                  >
                    <Ban size={16} className="inline -mt-0.5" />{" "}
                    <span className="ml-1">Void</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Money quick stats (match QuoteDetails; line-level discounts) */}
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <MoneyStat label="Subtotal" value={clientSubtotal} icon={DollarSign} />
            <MoneyStat label={taxHeaderLabel} value={taxTotal} icon={Percent} />
            {/* combined line + invoice-level discounts */}
            <MoneyStat
              label="Discounts"
              value={-combinedDiscountDisplay}
              icon={Percent}
            />
            <MoneyStat label="Total" value={computedTotal} icon={DollarSign} />
          </div>
        </div>

        {/* content */}
        <div className="p-4 sm:p-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Main */}
            <div className="lg:col-span-2 space-y-6">
              {/* Company + Meta (match QuoteDetails) */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {co.logoUrl ? (
                      <img
                        src={co.logoUrl}
                        alt={co.name || "Company"}
                        className="h-10 w-10 rounded-xl object-contain bg-white border border-slate-200"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
                        <Building2 size={18} />
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900">
                          {co.name || "Your Company"}
                        </span>
                        {canMutateRole() && (
                          <button
                            onClick={onRefreshCompanySnapshot}
                            className="inline-flex items-center justify-center rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-50"
                            title="Refresh from Company"
                          >
                            <RefreshCw size={14} />
                          </button>
                        )}
                      </div>
                      {co.legalName && (
                        <div className="text-sm text-slate-500">
                          {co.legalName}
                        </div>
                      )}
                      <div className="mt-1 text-xs text-slate-600">
                        {[co.email, co.phone].filter(Boolean).join(" · ") ||
                          "—"}
                      </div>
                      {co.address && (
                        <div className="mt-1 text-xs text-slate-600 whitespace-pre-line">
                          {fmtCoAddress(co.address)}
                        </div>
                      )}
                      {co.taxId && (
                        <div className="mt-1 text-xs text-slate-600">
                          Tax ID: {co.taxId}
                        </div>
                      )}
                      {co.website && (
                        <a
                          href={co.website}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-1 inline-block text-xs text-indigo-600 hover:underline"
                        >
                          {co.website}
                        </a>
                      )}
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 p-4">
                    <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm">
                      <div className="text-slate-500">Status</div>
                      <div className="flex justify-end">
                        <StatusChip status={derivedStatus} />
                      </div>

                      <div className="text-slate-500">Invoice #</div>
                      <div className="text-right font-semibold">
                        {invoice.invoiceNumber || "—"}
                      </div>

                      <div className="text-slate-500">Issue</div>
                      <div className="text-right flex items-center justify-end gap-1">
                        <CalendarDays
                          size={14}
                          className="text-slate-400"
                        />{" "}
                        {fmtDateOnlyUTC(invoice.issueDate)}
                      </div>

                      <div className="text-slate-500">Due</div>
                      <div className="text-right flex items-center justify-end gap-1">
                        <CalendarDays
                          size={14}
                          className="text-slate-400"
                        />{" "}
                        {fmtDateOnlyUTC(invoice.dueDate)}
                      </div>

                      <div className="text-slate-500">Total</div>
                      <div className="text-right font-semibold">
                        {fmtMoney(computedTotal, currency)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bill To (with right-aligned multiline address) */}
                <div className="mt-6">
                  <div className="text-sm font-semibold text-slate-800">
                    Bill To
                  </div>
                  <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Left: identity + contact */}
                      <div>
                        <div className="flex items-center gap-2 text-slate-800">
                          <User2 size={16} className="text-slate-500" />
                          <span className="font-medium">
                            {billToCompany}
                          </span>
                          {client?._id ? (
                            <Link
                              className="ml-2 text-indigo-600 text-sm hover:underline"
                              to={`/clients/${client._id}`}
                            >
                              View client
                            </Link>
                          ) : null}
                        </div>

                        {billToContactName && (
                          <div className="mt-1 text-sm text-slate-700">
                            {billToContactName}
                          </div>
                        )}

                        {(billToEmail || billToPhone) && (
                          <div className="mt-1 text-sm text-slate-600 flex flex-col gap-0.5">
                            {billToEmail && (
                              <a
                                className="hover:underline"
                                href={`mailto:${billToEmail}`}
                              >
                                {billToEmail}
                              </a>
                            )}
                            {billToPhone && (
                              <a
                                className="hover:underline"
                                href={`tel:${billToPhone}`}
                              >
                                {billToPhone}
                              </a>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Right: address */}
                      {billToAddressText && (
                        <div className="md:text-right text-sm text-slate-700 whitespace-pre-line">
                          {billToAddressText}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-base font-semibold text-slate-900 mb-4">
                  Items & Services
                </div>
                <div className="overflow-hidden rounded-xl border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr className="text-left text-sm text-slate-600">
                        <th className="px-4 py-2 font-semibold">
                          Description
                        </th>
                        <th className="px-4 py-2 font-semibold w-24 text-right">
                          Qty
                        </th>
                        <th className="px-4 py-2 font-semibold w-32 text-right">
                          Rate
                        </th>
                        <th className="px-4 py-2 font-semibold w-32 text-right">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {(invoice?.lineItems || []).map((li, idx) => {
                        const qty = Math.max(0, Number(li.quantity || 0));
                        const price = Number(li.unitPrice ?? li.rate ?? 0);
                        const c = calcLine(li); // line total (after discount + tax)

                        return (
                          <tr key={idx} className="text-sm">
                            <td className="px-4 py-3">
                              <div className="font-medium text-slate-900">
                                {li.description || li.name || "Item"}
                              </div>
                              {li?.notes ? (
                                <div className="text-slate-500">
                                  {li.notes}
                                </div>
                              ) : null}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums">
                              {qty}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums">
                              {fmtMoney(price, currency)}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums">
                              {fmtMoney(c.total, currency)}
                            </td>
                          </tr>
                        );
                      })}
                      {!invoice?.lineItems?.length && (
                        <tr>
                          <td
                            className="px-4 py-6 text-slate-500 text-sm"
                            colSpan={4}
                          >
                            No line items.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Side total card */}
                <div className="mt-6 flex justify-end">
                  <div className="w-full sm:w-96 rounded-xl border border-slate-200 bg-white p-4">
                    <div className="flex items-center justify-between text-sm text-slate-600">
                      <span>Subtotal</span>
                      <span className="tabular-nums">
                        {fmtMoney(clientSubtotal, currency)}
                      </span>
                    </div>

                    <div className="mt-2 flex items-center justify-between text-sm text-slate-600">
                      <span>Discounts</span>
                      {/* combined line + invoice discount */}
                      <span className="tabular-nums">
                        -{fmtMoney(combinedDiscountDisplay, currency)}
                      </span>
                    </div>

                    {/* mixed rates breakdown */}
                    {taxBreakdownRows.length > 1 && (
                      <div className="mt-2 space-y-1 text-sm text-slate-600">
                        {taxBreakdownRows.map(([pct, amt]) => (
                          <div
                            key={pct}
                            className="flex items-center justify-between"
                          >
                            <span>{`Tax ${pct}%`}</span>
                            <span className="tabular-nums">
                              {fmtMoney(amt, currency)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-2 flex items-center justify-between text-sm text-slate-600">
                      <span>{taxHeaderLabel}</span>
                      <span className="tabular-nums">
                        {fmtMoney(taxTotal, currency)}
                      </span>
                    </div>

                    <div className="mt-3 border-t pt-3 flex items-center justify-between text-base font-semibold text-slate-900">
                      <span>Total</span>
                      <span className="tabular-nums">
                        {fmtMoney(computedTotal, currency)}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-sm text-slate-600">
                      <span>Amount Paid</span>
                      <span className="tabular-nums">
                        {fmtMoney(totalPaid, currency)}
                      </span>
                    </div>
                    <div className="mt-3 border-t pt-3 flex items-center justify-between text-base font-semibold">
                      <span>Amount Due</span>
                      <span
                        className={cx(
                          "tabular-nums",
                          balance > 0
                            ? "text-rose-600"
                            : "text-emerald-600"
                        )}
                      >
                        {fmtMoney(balance, currency)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payments */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="text-base font-semibold text-slate-900">
                    Payments
                  </div>
                  {canMutateRole() && (
                    <button
                      onClick={() =>
                        !isPaymentLocked ? setShowAddPayment(true) : null
                      }
                      disabled={isPaymentLocked}
                      className={cx(
                        "inline-flex items-center gap-2 rounded-lg px-3 py-2",
                        isPaymentLocked
                          ? "bg-indigo-200/40 text-indigo-900/40 cursor-not-allowed"
                          : "bg-indigo-600 text-white hover:bg-indigo-700"
                      )}
                      title={
                        isPaymentLocked
                          ? "Payments are disabled on void/written-off invoices"
                          : "Record payment"
                      }
                    >
                      <CreditCard size={16} />
                      Record Payment
                    </button>
                  )}
                </div>

                <div className="mt-3 overflow-hidden rounded-xl border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50 text-sm text-slate-600">
                      <tr>
                        <th className="px-4 py-2 text-left font-semibold">
                          Date
                        </th>
                        <th className="px-4 py-2 text-left font-semibold">
                          Method
                        </th>
                        <th className="px-4 py-2 text-left font-semibold">
                          Source
                        </th>
                        <th className="px-4 py-2 text-left font-semibold">
                          Status
                        </th>
                        <th className="px-4 py-2 text-left font-semibold">
                          Reference
                        </th>
                        <th className="px-4 py-2 text-right font-semibold">
                          Amount
                        </th>
                        <th className="px-4 py-2 text-right font-semibold">
                          Running Balance
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white text-sm">
                      {paymentsWithBalance.length ? (
                        paymentsWithBalance.map((p, idx) => (
                          <tr key={p._id || idx}>
                            <td className="px-4 py-3">
                              {fmtDate(p.paymentDate)}
                            </td>
                            <td className="px-4 py-3">
                              {p.method || "—"}
                            </td>
                            <td className="px-4 py-3">
                              {p.source || "—"}
                            </td>
                            <td className="px-4 py-3 capitalize">
                              {p.status || "succeeded"}
                            </td>
                            <td className="px-4 py-3">
                              {p.reference || "—"}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums">
                              {fmtMoney(p.amount || 0, currency)}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums">
                              {fmtMoney(p.runningBalance || 0, currency)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            className="px-4 py-6 text-slate-500"
                            colSpan={7}
                          >
                            No payments recorded.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Notes & Activity */}
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="text-base font-semibold text-slate-900 mb-2">
                    Customer Notes
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">
                    {invoice?.notes || "—"}
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="text-base font-semibold text-slate-900 mb-2">
                    Activity
                  </div>
                  <ul className="space-y-2 text-sm text-slate-700">
                    <li>Created: {fmtDateLocal(invoice?.createdAt)}</li>
                    {invoice?.sentAt && (
                      <li>Sent: {fmtDateLocal(invoice.sentAt)}</li>
                    )}
                    {lastSentAt && (
                      <li>Last Sent: {fmtDateLocal(lastSentAt)}</li>
                    )}
                    {(invoice?.viewedAt || lastViewAt) && (
                      <li>
                        Last Viewed:{" "}
                        {fmtDateLocal(invoice?.viewedAt || lastViewAt)}
                      </li>
                    )}
                    {invoice?.updatedAt && (
                      <li>Updated: {fmtDateLocal(invoice.updatedAt)}</li>
                    )}
                    {typeof viewCount === "number" && (
                      <li>Public Views: {viewCount}</li>
                    )}
                    {typeof emailStats?.payClickCount === "number" && (
                      <li>Pay Clicks: {emailStats?.payClickCount}</li>
                    )}
                    {invoice?.lifecycleStatus && (
                      <li>
                        Lifecycle:{" "}
                        {String(invoice.lifecycleStatus).toUpperCase()}
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-base font-semibold text-slate-900">
                  Invoice Meta
                </div>
                <dl className="mt-3 space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <dt className="text-slate-600">Currency</dt>
                    <dd className="font-medium">{currency}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-slate-600">Terms</dt>
                    <dd className="font-medium">{terms}</dd>
                  </div>
                  {invoice?.poNumber && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">PO #</dt>
                      <dd className="font-medium">{invoice.poNumber}</dd>
                    </div>
                  )}
                  {invoice?.assignedTo?.name && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Owner</dt>
                      <dd className="font-medium">
                        {invoice.assignedTo.name}
                      </dd>
                    </div>
                  )}
                </dl>

                {/* 👇 Expose invoiceDetails (server calc snapshot) */}
                {invoice?.invoiceDetails && (
                  <div className="mt-4 border-t border-dashed border-slate-200 pt-3">
                    <div className="text-xs font-semibold uppercase text-slate-500 tracking-wide">
                      Invoice details
                    </div>
                    <dl className="mt-2 space-y-1 text-xs">
                      {invoice.invoiceDetails.subtotalBase != null && (
                        <div className="flex items-center justify-between">
                          <dt className="text-slate-600">Base subtotal</dt>
                          <dd className="font-medium tabular-nums">
                            {fmtMoney(
                              invoice.invoiceDetails.subtotalBase,
                              currency
                            )}
                          </dd>
                        </div>
                      )}
                      {invoice.invoiceDetails.lineDiscountTotal != null && (
                        <div className="flex items-center justify-between">
                          <dt className="text-slate-600">Line discounts</dt>
                          <dd className="font-medium tabular-nums">
                            -
                            {fmtMoney(
                              invoice.invoiceDetails.lineDiscountTotal,
                              currency
                            )}
                          </dd>
                        </div>
                      )}
                      {invoice.invoiceDetails.discountTotal != null &&
                        Number(invoice.invoiceDetails.discountTotal) > 0 && (
                          <div className="flex items-center justify-between">
                            <dt className="text-slate-600">
                              Invoice discount
                            </dt>
                            <dd className="font-medium tabular-nums">
                              -
                              {fmtMoney(
                                invoice.invoiceDetails.discountTotal,
                                currency
                              )}
                            </dd>
                          </div>
                        )}
                      {invoice.invoiceDetails.taxTotal != null && (
                        <div className="flex items-center justify-between">
                          <dt className="text-slate-600">Tax</dt>
                          <dd className="font-medium tabular-nums">
                            {fmtMoney(
                              invoice.invoiceDetails.taxTotal,
                              currency
                            )}
                          </dd>
                        </div>
                      )}
                      {invoice.invoiceDetails.totalAmount != null && (
                        <div className="flex items-center justify-between">
                          <dt className="text-slate-600">Total</dt>
                          <dd className="font-semibold tabular-nums">
                            {fmtMoney(
                              invoice.invoiceDetails.totalAmount,
                              currency
                            )}
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>
                )}

                {canEdit && (
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    {/* Quick Edit (modal) */}
                    <button
                      onClick={() => setShowEdit(true)}
                      className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-slate-700 hover:bg-slate-50"
                    >
                      <Edit2 size={16} /> Edit
                    </button>

                    <button
                      onClick={onDelete}
                      className="inline-flex items-center justify-center gap-2 rounded-lg border border-rose-200 px-3 py-2 text-rose-600 hover:bg-rose-50"
                    >
                      <Trash2 size={16} /> Delete
                    </button>
                  </div>
                )}
              </div>

              {/* Email Activity (same placement as QuoteDetails) */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-base font-semibold text-slate-900">
                  Email Activity
                </div>
                <dl className="mt-3 space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <dt className="text-slate-600">Status</dt>
                    <dd className="font-medium">
                      {lastSentAt ? "Sent" : "Not sent yet"}
                    </dd>
                  </div>

                  {lastSentAt && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Last Sent</dt>
                      <dd className="font-medium">
                        {fmtDateTimeLocal(lastSentAt)}
                      </dd>
                    </div>
                  )}

                  {typeof recipientsCount === "number" &&
                    recipientsCount >= 0 && (
                      <div className="flex items-center justify-between">
                        <dt className="text-slate-600">Recipients</dt>
                        <dd className="font-medium">
                          {recipientsCount}
                        </dd>
                      </div>
                    )}

                  {deliveredCount > 0 && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Delivered</dt>
                      <dd className="font-medium">
                        {deliveredCount}
                        {lastDeliveredAt ? (
                          <span className="text-slate-500 font-normal">
                            {" "}
                            · {fmtDateTimeLocal(lastDeliveredAt)}
                          </span>
                        ) : null}
                      </dd>
                    </div>
                  )}

                  {openCount > 0 && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Opened</dt>
                      <dd className="font-medium">
                        {openCount}
                        {lastOpenAt ? (
                          <span className="text-slate-500 font-normal">
                            {" "}
                            · {fmtDateTimeLocal(lastOpenAt)}
                          </span>
                        ) : null}
                      </dd>
                    </div>
                  )}

                  {clickCount > 0 && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Clicked</dt>
                      <dd className="font-medium">
                        {clickCount}
                        {lastClickAt ? (
                          <span className="text-slate-500 font-normal">
                            {" "}
                            · {fmtDateTimeLocal(lastClickAt)}
                          </span>
                        ) : null}
                      </dd>
                    </div>
                  )}

                  {bouncedCount > 0 && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Bounced</dt>
                      <dd className="font-medium">
                        {bouncedCount}
                        {lastBouncedAt ? (
                          <span className="text-slate-500 font-normal">
                            {" "}
                            · {fmtDateTimeLocal(lastBouncedAt)}
                          </span>
                        ) : null}
                      </dd>
                    </div>
                  )}

                  {droppedCount > 0 && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Dropped</dt>
                      <dd className="font-medium">
                        {droppedCount}
                        {lastDroppedAt ? (
                          <span className="text-slate-500 font-normal">
                            {" "}
                            · {fmtDateTimeLocal(lastDroppedAt)}
                          </span>
                        ) : null}
                      </dd>
                    </div>
                  )}

                  {complainedCount > 0 && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Complaints</dt>
                      <dd className="font-medium">
                        {complainedCount}
                        {lastComplainedAt ? (
                          <span className="text-slate-500 font-normal">
                            {" "}
                            · {fmtDateTimeLocal(lastComplainedAt)}
                          </span>
                        ) : null}
                      </dd>
                    </div>
                  )}

                  {failedCount > 0 && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Failed</dt>
                      <dd className="font-medium">
                        {failedCount}
                        {lastFailedAt ? (
                          <span className="text-slate-500 font-normal">
                            {" "}
                            · {fmtDateTimeLocal(lastFailedAt)}
                          </span>
                        ) : null}
                      </dd>
                    </div>
                  )}

                  {viewCount > 0 && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Public Views</dt>
                      <dd className="font-medium">
                        {viewCount}
                        {lastViewAt ? (
                          <span className="text-slate-500 font-normal">
                            {" "}
                            · {fmtDateTimeLocal(lastViewAt)}
                          </span>
                        ) : null}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Linked Records */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-base font-semibold text-slate-900">
                  Linked Records
                </div>

                {/* Job row */}
                <div className="mt-3 flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2">
                  <div className="text-sm">
                    <div className="font-medium text-slate-900">Job</div>
                    {linkedWork ? (
                      <div className="text-slate-600">
                        #{linkedWork.workNumber || "—"} ·{" "}
                        {linkedWork.status || "—"}
                      </div>
                    ) : (
                      <div className="text-slate-500">No job linked</div>
                    )}
                  </div>
                  <div>
                    {linkedWork ? (
                      <Link
                        to={`/work/${linkedWork._id || linkedWork.id}`}
                        className="inline-flex items-center rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        View
                      </Link>
                    ) : null}
                  </div>
                </div>

                {/* Quote row */}
                <div className="mt-2 flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2">
                  <div className="text-sm">
                    <div className="font-medium text-slate-900">Quote</div>
                    {linkedQuote ? (
                      <div className="text-slate-600">
                        #{linkedQuote.quoteNumber || "—"} ·{" "}
                        {linkedQuote.status || "—"}{" "}
                        {Number.isFinite(linkedQuote.totalAmount) && (
                          <span className="ml-1">
                            ·{" "}
                            {fmtMoney(
                              linkedQuote.totalAmount,
                              currency
                            )}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="text-slate-500">No quote linked</div>
                    )}
                  </div>
                  <div>
                    {linkedQuote ? (
                      <Link
                        to={`/quotes/${linkedQuote._id}`}
                        className="inline-flex items-center rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        View
                      </Link>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* Removed: Addresses card / Attachments card */}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showAddPayment && (
        <AddPaymentModal
          isOpen={showAddPayment}
          onClose={() => setShowAddPayment(false)}
          invoiceId={invoice._id}
          clientId={invoice.clientId}
          onSuccess={load}
        />
      )}

      {showEdit && (
        <EditInvoiceModal
          isOpen={showEdit}
          onClose={() => setShowEdit(false)}
          invoice={invoice}
          onUpdated={load}
        />
      )}

      {showSend && (
        <SendInvoiceModal
          isOpen={showSend}
          onClose={() => setShowSend(false)}
          invoice={invoice}
          /** pass lastSentAt so modal can show “3h ago / (within window)” just like Quote */
          lastSentAt={lastSentAt}
          /** use onSent (modal’s callback) */
          onSent={() => {
            setShowSend(false);
            toast.success("Email queued");
            load();
          }}
        />
      )}
    </div>
  );
}
